import * as React from 'react'
import { Modal, Alert, Button } from 'antd'
import { Currency } from '../../components/Currency'
import { CreateInvoice, InvoiceStatus, CreateInvoiceLine } from '@getgreenline/homi-shared'

interface Props {
  visible: boolean
  createInvoice: CreateInvoice
  updatingNewStatus: InvoiceStatus
  isStatusUpdating: boolean
  setIsModalVisible: (isModalVisible: boolean) => void
  updateStatus: () => void
  setUpdatingNewStatus: (updatingNewStatus?: InvoiceStatus) => void
}

export const PurchaseOrderModal = (props: Props) => {
  const {
    visible,
    createInvoice,
    updatingNewStatus,
    isStatusUpdating,
    setIsModalVisible,
    updateStatus,
    setUpdatingNewStatus,
  } = props

  const {
    lines,
    totalUnitCount,
    total,
    productsWithZeroCostPerUnit,
    productsWithNoQtyReceived,
    productsWithNegativeMargin,
    isTaxGroupSelected,
  } = createInvoice

  const defaultVisibilityThreshold = 5

  const [zeroCostPerUnitThreshold, setZeroCostPerUnitThreshold] = React.useState(
    defaultVisibilityThreshold,
  )
  const [noQtyReceivedThreshold, setNoQtyReceivedThreshold] = React.useState(
    defaultVisibilityThreshold,
  )
  const [negativeMarginThreshold, setNegativeMarginThreshold] = React.useState(
    defaultVisibilityThreshold,
  )

  const title = updatingNewStatus === InvoiceStatus.COMPLETED ? 'Complete' : 'Cancel'

  const okButtonType = updatingNewStatus === InvoiceStatus.COMPLETED ? 'primary' : 'danger'

  const isOKButtonDisabled =
    updatingNewStatus === InvoiceStatus.COMPLETED && productsWithNoQtyReceived.length > 0

  const closeModal = () => {
    setIsModalVisible(false)
    setUpdatingNewStatus()
  }

  const getNewThreshold = (threshold: number, targetLines: CreateInvoiceLine[]): number => {
    return threshold === defaultVisibilityThreshold
      ? targetLines.length
      : defaultVisibilityThreshold
  }

  const noAlertsPresent =
    productsWithNoQtyReceived.length === 0 &&
    productsWithZeroCostPerUnit.length === 0 &&
    productsWithNegativeMargin.length === 0 &&
    isTaxGroupSelected

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={closeModal}
      footer={[
        <Button key={0} onClick={closeModal}>
          Close
        </Button>,
        <Button
          key={1}
          type={okButtonType}
          disabled={isOKButtonDisabled}
          onClick={updateStatus}
          loading={isStatusUpdating}
        >
          {title}
        </Button>,
      ]}
    >
      {updatingNewStatus === InvoiceStatus.COMPLETED && (
        <>
          {productsWithNoQtyReceived.length > 0 && (
            <Alert
              type='error'
              className='mb-3'
              message={
                <div className='d-flex flex-row justify-content-between'>
                  <span>Missing quantity received</span>

                  {productsWithNoQtyReceived.length > defaultVisibilityThreshold && (
                    <Button
                      size='small'
                      icon='eye'
                      style={{ width: 'fit-content' }}
                      onClick={() => {
                        const threshold = getNewThreshold(
                          noQtyReceivedThreshold,
                          productsWithNoQtyReceived,
                        )

                        setNoQtyReceivedThreshold(threshold)
                      }}
                    >
                      {noQtyReceivedThreshold === defaultVisibilityThreshold ? 'All' : 'Less'}
                    </Button>
                  )}
                </div>
              }
              description={
                <ul className='no-margin-list'>
                  {productsWithNoQtyReceived
                    .slice(0, noQtyReceivedThreshold)
                    .map((product, index) => (
                      <li key={index}>{product.productName}</li>
                    ))}
                </ul>
              }
              showIcon
            />
          )}

          {productsWithZeroCostPerUnit.length > 0 && (
            <Alert
              type='warning'
              className='mb-3'
              message={
                <div className='d-flex flex-row justify-content-between align-items-center'>
                  <span>Products with $0.00 cost per unit</span>

                  {productsWithZeroCostPerUnit.length > defaultVisibilityThreshold && (
                    <Button
                      size='small'
                      icon='eye'
                      onClick={() => {
                        const threshold = getNewThreshold(
                          zeroCostPerUnitThreshold,
                          productsWithZeroCostPerUnit,
                        )

                        setZeroCostPerUnitThreshold(threshold)
                      }}
                    >
                      {zeroCostPerUnitThreshold === defaultVisibilityThreshold ? 'All' : 'Less'}
                    </Button>
                  )}
                </div>
              }
              description={
                <ul className='no-margin-list'>
                  {productsWithZeroCostPerUnit
                    .slice(0, zeroCostPerUnitThreshold)
                    .map((product, index) => (
                      <li key={index}>{product.productName}</li>
                    ))}
                </ul>
              }
              showIcon
            />
          )}

          {productsWithNegativeMargin.length > 0 && (
            <Alert
              type='warning'
              className='mb-3'
              message={
                <div className='d-flex flex-row justify-content-between align-items-center'>
                  <span>Cost greater than retail price</span>

                  {productsWithNegativeMargin.length > defaultVisibilityThreshold && (
                    <Button
                      size='small'
                      icon='eye'
                      onClick={() => {
                        const threshold = getNewThreshold(
                          negativeMarginThreshold,
                          productsWithNegativeMargin,
                        )

                        setNegativeMarginThreshold(threshold)
                      }}
                    >
                      {negativeMarginThreshold === defaultVisibilityThreshold ? 'All' : 'Less'}
                    </Button>
                  )}
                </div>
              }
              description={
                <ul className='no-margin-list'>
                  {productsWithNegativeMargin
                    .slice(0, negativeMarginThreshold)
                    .map((product, index) => (
                      <li key={index}>{product.productName}</li>
                    ))}
                </ul>
              }
              showIcon
            />
          )}

          {!isTaxGroupSelected && (
            <Alert
              type='warning'
              className='mb-3'
              message='No taxes attached'
              description='Select a tax group to help track PO taxes.'
              showIcon
            />
          )}

          <Alert
            type='success'
            className={`mt-${noAlertsPresent ? '0' : '2'}`}
            message='Confirmation'
            description={
              <div>
                <div>{`You are checking in ${totalUnitCount} units for ${lines.length} product${
                  lines.length > 1 ? 's' : ''
                }.`}</div>

                <div>
                  <span>Total cost: </span>
                  <Currency centValue={total} />
                </div>
              </div>
            }
            showIcon
          />
        </>
      )}

      {updatingNewStatus === InvoiceStatus.CANCELLED && (
        <Alert
          type='error'
          message='Cancel purchase order'
          description='This purchase order will be deleted and cannot be recovered. Inventory will not be checked in. Are you sure?'
          showIcon
        />
      )}
    </Modal>
  )
}
